'use client';

/**
 * Story 14.4 (AC-4.3, AC-4.7) — tag the active Sentry scope with `customer_id`.
 *
 * Mounted inside ClerkProvider in `src/app/layout.tsx` so `useOrganization()`
 * can resolve the active org. The single safe identifier is `customer_id`
 * (opaque UUID surfaced via the JWT template's `org_metadata.customer_id`,
 * mirrored in Clerk's organization `publicMetadata`).
 *
 * PII denylist — these MUST NOT reach Sentry from the FE either (Pass-2 m7):
 *   - `Sentry.setUser(...)` — triggers Sentry's PII handling path even with
 *     `sendDefaultPii: false`.
 *   - `email`, `org_id` (Clerk-internal), IP address, JWT tokens, raw bodies.
 *
 * Safe to render even when `NEXT_PUBLIC_SENTRY_DSN` is empty (dev / tests) —
 * `Sentry.setTag` is a no-op against an uninitialised SDK.
 */

import { useEffect } from 'react';
import { useOrganization } from '@clerk/nextjs';
import * as Sentry from '@sentry/nextjs';

export function SentryAuthSync(): null {
  const { organization, isLoaded } = useOrganization();

  useEffect(() => {
    if (!isLoaded) return;
    const meta = organization?.publicMetadata as { customer_id?: unknown } | undefined;
    const customerId = meta?.customer_id;
    if (typeof customerId === 'string' && customerId) {
      Sentry.setTag('customer_id', customerId);
    } else {
      // CR P2: sign-out or org-switch to a context without customer_id MUST
      // clear the prior tenant's tag from the browser-side Sentry global
      // scope. Pre-P2: the useEffect's guard fell through silently → tag
      // persisted forever → anonymous-user errors (e.g. the sign-out flow
      // itself, navigation errors during redirect) reported as the prior
      // tenant. Pass-2 m7 denylist relies on `customer_id` being correct.
      Sentry.setTag('customer_id', null);
    }
  }, [isLoaded, organization]);

  return null;
}
